import { useEffect, useState, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../api/client";

const CSS = `
.dash-metrics { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:20px; }
@media(max-width:800px){ .dash-metrics{ grid-template-columns:repeat(2,1fr); } }
@media(max-width:480px){ .dash-metrics{ grid-template-columns:1fr; } }
.dash-metric { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:18px 20px; }
.dash-metric-label { font-size:11px; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#6080aa; margin-bottom:6px; }
.dash-metric-value { font-size:22px; font-weight:700; letter-spacing:-.02em; font-variant-numeric:tabular-nums; }
.dash-metric-sub   { font-size:11px; color:#6080aa; margin-top:4px; }

.dash-row2 { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
@media(max-width:700px){ .dash-row2{ grid-template-columns:1fr; } }
.dash-row3 { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
@media(max-width:700px){ .dash-row3{ grid-template-columns:1fr; } }

.dash-card { background:#ffffff; border:1px solid #c0d0eb; border-radius:10px; padding:20px; }
.dash-card-title { font-size:13px; font-weight:600; color:#0d1a36; margin-bottom:14px; }

.dash-bar-row { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.dash-bar-label { font-size:12px; color:#2a4a78; width:90px; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dash-bar-track { flex:1; height:6px; background:#f4f8ff; border-radius:3px; overflow:hidden; }
.dash-bar-fill  { height:100%; border-radius:3px; background:#059669; transition:width .4s; }
.dash-bar-fill.over { background:#dc2626; }
.dash-bar-val   { font-size:12px; color:#2563eb; font-variant-numeric:tabular-nums; width:80px; text-align:right; flex-shrink:0; }

.dash-receipt-item { display:flex; justify-content:space-between; align-items:center;
  padding:9px 12px; background:#f4f8ff; border-radius:7px; margin-bottom:6px; }
.dash-receipt-shop { font-size:13px; font-weight:600; color:#0d1a36; }
.dash-receipt-date { font-size:11px; color:#6080aa; margin-top:2px; }
.dash-receipt-amt  { font-size:13px; font-weight:700; color:#059669; white-space:nowrap; }

.dash-shop-row { display:flex; align-items:center; gap:10px; margin-bottom:8px; }
.dash-shop-name { font-size:12px; color:#2a4a78; width:100px; flex-shrink:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dash-shop-track{ flex:1; height:5px; background:#f4f8ff; border-radius:3px; overflow:hidden; }
.dash-shop-fill { height:100%; border-radius:3px; background:#2563eb; }
.dash-shop-val  { font-size:11px; color:#6080aa; width:70px; text-align:right; flex-shrink:0; font-variant-numeric:tabular-nums; }

.dash-item-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:8px; }
.dash-item-card { background:#f4f8ff; border-radius:8px; padding:12px; text-align:center; }
.dash-item-cat  { font-size:10px; color:#6080aa; font-weight:600; text-transform:uppercase; letter-spacing:.05em; margin-bottom:4px; }
.dash-item-name { font-size:12px; color:#0d1a36; font-weight:600; margin-bottom:6px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dash-item-price{ font-size:14px; color:#059669; font-weight:700; font-variant-numeric:tabular-nums; }
.dash-item-shop { font-size:10px; color:#6080aa; margin-top:2px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

.dash-empty { font-size:13px; color:#6080aa; }
.dash-spinner-wrap { display:flex; justify-content:center; align-items:center; height:200px; }
.dash-spinner { width:32px; height:32px; border:3px solid #c0d0eb; border-top-color:#059669; border-radius:50%; animation:dspin .6s linear infinite; }
@keyframes dspin { to{ transform:rotate(360deg); } }
`;

export default function Dashboard() {
  const [summary,       setSummary]       = useState([]);
  const [totalSpent,    setTotalSpent]    = useState([]);
  const [monthly,       setMonthly]       = useState([]);
  const [budgets,       setBudgets]       = useState([]);
  const [shopData,      setShopData]      = useState([]);
  const [recentRcpts,   setRecentRcpts]   = useState([]);
  const [recentItems,   setRecentItems]   = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");
  const [remapping,     setRemapping]     = useState(false);
  const [remapDone,     setRemapDone]     = useState(false);

  const month = new Date().toISOString().slice(0, 7);

  const load = useCallback(() => {
    Promise.all([
      api.get(`/expense/summary?month=${month}`),
      api.get(`/expense/total-spent?month=${month}`),
      api.get("/expense/monthly-totals"),
      api.get(`/expense/budgets?month=${month}`),
      api.get(`/expense/shop-spending?month=${month}`),
      api.get("/expense/recent-receipts?limit=5"),
      api.get("/expense/recent-items?limit=5"),
    ]).then(([s, ts, m, b, sh, rr, ri]) => {
      setSummary(s.data); setTotalSpent(ts.data); setMonthly(m.data); setBudgets(b.data);
      setShopData(sh.data); setRecentRcpts(rr.data); setRecentItems(ri.data);
    }).catch(e => setError(e?.message || "Failed to load dashboard."))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const spentByCurrency = {};
  summary.forEach(r => {
    const c = r.currency || "OMR";
    spentByCurrency[c] = (spentByCurrency[c] || 0) + parseFloat(r.total);
  });
  // Receipt-level total (includes VAT) — used for the "Month Spending" metric specifically.
  const vatSpentByCurrency = {};
  totalSpent.forEach(r => {
    const c = r.currency || "OMR";
    vatSpentByCurrency[c] = (vatSpentByCurrency[c] || 0) + parseFloat(r.total);
  });
  const budgetByCurrency = {};
  budgets.forEach(b => {
    const c = b.currency || "OMR";
    budgetByCurrency[c] = (budgetByCurrency[c] || 0) + parseFloat(b.amount);
  });
  const vatSpendCurrencies = Object.keys(vatSpentByCurrency);
  const budgetCurrencies = Object.keys(budgetByCurrency);
  const topCat = summary[0];

  const ttStyle = { background:"#ffffff", border:"1px solid #c0d0eb", color:"#0d1a36" };
  const maxShop = shopData.length ? parseFloat(shopData[0].total) : 1;

  if (loading) return <><style>{CSS}</style><div className="dash-spinner-wrap"><div className="dash-spinner"/></div></>;

  return (
    <>
      <style>{CSS}</style>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20,flexWrap:'wrap',gap:10}}>
        <div style={{fontSize:18,fontWeight:700,color:'#0d1a36'}}>🏠 Dashboard — {month}</div>
        {!remapDone && (
          <button
            style={{background:'#c0d0eb',border:'1px solid #2563eb',borderRadius:7,padding:'7px 14px',fontSize:12,
                    color:'#2a4a78',cursor:remapping?'not-allowed':'pointer',opacity:remapping?.5:1,fontFamily:'inherit'}}
            disabled={remapping}
            onClick={async()=>{
              setRemapping(true);
              try { await api.post("/expense/remap-categories"); setRemapDone(true); load(); }
              catch(e){ setError("Remap failed: "+e.message); }
              finally{ setRemapping(false); }
            }}>
            {remapping ? "Fixing…" : "🔧 Fix Old Categories"}
          </button>
        )}
        {remapDone && <span style={{fontSize:12,color:'#059669'}}>✓ Categories updated</span>}
      </div>

      {error && <div style={{padding:'12px 16px',borderRadius:8,background:'rgba(248,113,113,.08)',border:'1px solid rgba(248,113,113,.2)',color:'#dc2626',fontSize:13,marginBottom:16}}>⚠ {error}</div>}

      {/* ── 4 Metrics ── */}
      <div className="dash-metrics">
        <div className="dash-metric">
          <div className="dash-metric-label">💸 Month Spending</div>
          {vatSpendCurrencies.length === 0
            ? <div className="dash-metric-value" style={{color:'#dc2626'}}>OMR 0.000</div>
            : vatSpendCurrencies.map(c => (
              <div key={c} className="dash-metric-value" style={{color:'#dc2626',fontSize:vatSpendCurrencies.length>1?16:22}}>
                {c} {vatSpentByCurrency[c].toFixed(3)}
              </div>
            ))
          }
        </div>
        <div className="dash-metric">
          <div className="dash-metric-label">💰 Budget Remaining</div>
          {budgetCurrencies.length === 0
            ? <div className="dash-metric-value" style={{color:'#2a4a78'}}>No budget set</div>
            : budgetCurrencies.map(c => {
              const rem = budgetByCurrency[c] - (spentByCurrency[c] || 0);
              return (
                <div key={c}>
                  <div className="dash-metric-value" style={{color:rem>=0?'#059669':'#dc2626',fontSize:budgetCurrencies.length>1?16:22}}>
                    {c} {Math.abs(rem).toFixed(3)}{rem<0?" over":""}
                  </div>
                  <div className="dash-metric-sub">{((rem/budgetByCurrency[c])*100).toFixed(1)}% left</div>
                </div>
              );
            })
          }
        </div>
        <div className="dash-metric">
          <div className="dash-metric-label">🧾 Receipts This Month</div>
          <div className="dash-metric-value" style={{color:'#2563eb'}}>{recentRcpts.length}</div>
        </div>
        <div className="dash-metric">
          <div className="dash-metric-label">🏆 Top Category</div>
          <div className="dash-metric-value" style={{color:'#b45309',fontSize:16}}>{topCat?.category || "—"}</div>
          {topCat && <div className="dash-metric-sub">{topCat.currency||"OMR"} {parseFloat(topCat.total).toFixed(3)}</div>}
        </div>
      </div>

      {/* ── Row 2: Categories + Monthly Trend ── */}
      <div className="dash-row2">
        <div className="dash-card">
          <div className="dash-card-title">🗂️ Top Categories</div>
          {summary.length ? summary.slice(0,6).map(s => {
            const rowCurrency = s.currency || "OMR";
            const rowCurrencyTotal = spentByCurrency[rowCurrency] || 0;
            const pct = rowCurrencyTotal ? Math.min((parseFloat(s.total)/rowCurrencyTotal)*100, 100) : 0;
            const budget = budgets.find(b=>b.category===s.category && (b.currency||"OMR")===rowCurrency);
            const over = budget && parseFloat(s.total) > parseFloat(budget.amount);
            return (
              <div className="dash-bar-row" key={s.category}>
                <span className="dash-bar-label">{s.category}</span>
                <div className="dash-bar-track"><div className={`dash-bar-fill${over?" over":""}`} style={{width:`${pct}%`}}/></div>
                <span className="dash-bar-val">{s.currency||"OMR"} {parseFloat(s.total).toFixed(3)}</span>
              </div>
            );
          }) : <div className="dash-empty">No spending data this month.</div>}
        </div>

        <div className="dash-card">
          <div className="dash-card-title">📈 Monthly Trend</div>
          {monthly.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthly.slice(-6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#c0d0eb" />
                <XAxis dataKey="month" stroke="#6080aa" tick={{fontSize:11}} />
                <YAxis stroke="#6080aa" tick={{fontSize:11}} />
                <Tooltip contentStyle={ttStyle} formatter={(v,n,props)=>[`${props?.payload?.currency||"OMR"} ${parseFloat(v).toFixed(3)}`,"Spent"]} />
                <Bar dataKey="total" radius={[3,3,0,0]}
                     fill="#2563eb"
                     label={false} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="dash-empty">No trend data yet.</div>}
        </div>
      </div>

      {/* ── Row 3: Recent Receipts + Top Retailers ── */}
      <div className="dash-row3">
        <div className="dash-card">
          <div className="dash-card-title">🧾 Recent Receipts</div>
          {recentRcpts.length ? recentRcpts.map(r => (
            <div className="dash-receipt-item" key={r.id}>
              <div>
                <div className="dash-receipt-shop">{r.shop_name || "Unknown Shop"}</div>
                <div className="dash-receipt-date">{String(r.receipt_date||"").slice(0,10)}</div>
              </div>
              <div className="dash-receipt-amt">{r.currency||"OMR"} {parseFloat(r.total_amount||0).toFixed(3)}</div>
            </div>
          )) : <div className="dash-empty">No receipts yet.</div>}
        </div>

        <div className="dash-card">
          <div className="dash-card-title">🏪 Top Retailers</div>
          {shopData.length ? shopData.slice(0,6).map(s => {
            const pct = (parseFloat(s.total)/maxShop)*100;
            return (
              <div className="dash-shop-row" key={s.shop_name}>
                <span className="dash-shop-name">{s.shop_name}</span>
                <div className="dash-shop-track"><div className="dash-shop-fill" style={{width:`${pct}%`}}/></div>
                <span className="dash-shop-val">{s.currency||"OMR"} {parseFloat(s.total).toFixed(3)}</span>
              </div>
            );
          }) : <div className="dash-empty">No shop data this month.</div>}
        </div>
      </div>

      {/* ── Recent Purchases ── */}
      {recentItems.length > 0 && (
        <div className="dash-card">
          <div className="dash-card-title">🛒 Recent Purchases</div>
          <div className="dash-item-grid">
            {recentItems.map((it, i) => (
              <div className="dash-item-card" key={i}>
                <div className="dash-item-cat">{it.category}</div>
                <div className="dash-item-name">{it.item_name}</div>
                <div className="dash-item-price">{it.currency||"OMR"} {parseFloat(it.total_price||0).toFixed(3)}</div>
                <div className="dash-item-shop">{it.shop_name}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
